import { useMemo } from 'react';

import { ApiService } from 'api/ApiService';
import type { FullTextSearchResult } from 'api/data/entities/projects/search/FullTextSearchResult';
import type { DefaultCasedOutputOperationResult } from 'api/outputOperationResult';
import { ENV_VARS } from 'app/config/environment-vars';
import { FULLTEXT_SEARCH_STATE } from 'state/fullTextSearch/fullTextSearchState';
import { GRID_STORE } from 'state/grids/gridState';
import { SELECTED_PROJECT_STORE } from 'state/selectedProject/selectedProjectState';

interface FullTextSearchServiceProps {
  search: (projectId: string, searchTerm: string) => Promise<DefaultCasedOutputOperationResult<FullTextSearchResult[]>>;
}

export class FullTextSearchService extends ApiService implements FullTextSearchServiceProps {
  constructor(url: string) {
    super(url, 'search');
  }

  async search(
    projectId: string,
    searchTerm: string
  ): Promise<DefaultCasedOutputOperationResult<FullTextSearchResult[]>> {
    try {
      FULLTEXT_SEARCH_STATE.getState().setIsLoading(true);

      const response = await this.instance.get<DefaultCasedOutputOperationResult<FullTextSearchResult[]>>(
        `?searchTerm=${searchTerm}`
      );

      if (response.data.Success) {
        GRID_STORE.getState().addNodeProperties(response.data.Result ?? []);

        const regexMatch = this.EscapeSpecialChars(searchTerm);

        FULLTEXT_SEARCH_STATE.getState().setResults(response.data.Result ?? [], regexMatch);
      }

      return response.data;
    } catch (error) {
      return error as DefaultCasedOutputOperationResult<FullTextSearchResult[]>;
    } finally {
      FULLTEXT_SEARCH_STATE.getState().setIsLoading(false);
    }
  }

  private EscapeSpecialChars(term: string) {
    const REGEXP_SPECIAL_CHAR_TO_REMOVE = /[*]/g;
    const REGEXP_SPECIAL_CHAR_TO_ESCAPE = /[!#$%^&)(+=.<>{}[\]:;'"|~`_-]/g;

    const escapedQuery = term.replace(REGEXP_SPECIAL_CHAR_TO_REMOVE, '').replace(REGEXP_SPECIAL_CHAR_TO_ESCAPE, '\\$&');

    return escapedQuery.split(' ');
  }
}

export const useFullTextSearchService = () => {
  const projectId = SELECTED_PROJECT_STORE((state) => state.projectId);

  return useMemo(() => {
    return new FullTextSearchService(`${ENV_VARS.DATA_URL}/api/projects/${projectId}/search`);
  }, [projectId]);
};
